
import { applicantStore, classifiersStore } from '@/store'
import { ValidationObserver } from 'vee-validate'
import { Component, Prop, Watch } from 'vue-property-decorator'
import Checkbox from '@/commons/components/core/Checkbox.vue'
import { ApplicationUtils } from '@/commons/utils/application'
import { ProviderInstance } from 'vee-validate/dist/types/types'
import { PlaceOfIssueUtils } from '@/commons/utils/place.of.issue'
import { translateValidationError } from '@/commons/configuration/vee.validate'
import RadioButtonRounded from '@/commons/components/core/RadioButtonRounded.vue'
import ModalBoxIsExpedited from '@/commons/components/modal-box/ModalBoxIsExpedited.vue'
import ModalBoxCourierDelivery from '@/commons/components/modal-box/ModalBoxCourierDelivery.vue'
import ClassifierSelectWithLabel from '@/commons/components/core/ClassifierSelectWithLabel.vue'
import SelectWithSearch from '@/commons/components/core/SelectWithSearch.vue'
import { ClassifiersUtils } from '@/commons/utils/classifiers'

import {
    ADULT_AGE,
    APPLICANT_MIN_AGE,
    ApplicantApplierRelationType,
    ApplicationDeliveryType,
    DocumentType,
    ValidDocumentType
} from '@/commons/constants/application'
import {
    ApplicationDocument,
    Classifier,
    ValidationError
} from '@/commons/types'
import ModalBoxMediatedIssuancePricing from '@/commons/components/modal-box/ModalBoxMediatedIssuancePricing.vue'
import { DocumentResponse } from '@/commons/types/document'
import { ObjectUtils } from '@/commons/utils/objects'
import { ENVIRONMENT } from '@/commons/configuration'
import { GeneralError } from '@/store/entities'
import { DocumentService } from '@/store/services/document.service'
import { ApplicationReason } from '@/commons/constants/classifier'
import AuthorizedPersonDetails from '@/commons/components/application-create/AuthorizedPersonDetails.vue'
import {
    ApplicationAuthorizedPersonEntity
} from '@/store/entities/application'
import ModalBoxWarning from '@/commons/components/modal-box/ModalBoxWarning.vue'
import InputWithLabel from '@/commons/components/core/InputWithLabel.vue'
import $ from 'jquery'
import CourierDetails from '@/commons/components/application-create/CourierDetails.vue'

@Component({
    components: {
        CourierDetails,
        InputWithLabel,
        ModalBoxWarning,
        AuthorizedPersonDetails,
        ModalBoxCourierDelivery,
        ModalBoxMediatedIssuancePricing,
        Checkbox,
        SelectWithSearch,
        ModalBoxIsExpedited,
        RadioButtonRounded,
        ClassifierSelectWithLabel
    }
})
export default class PlaceOfIssue extends ValidationObserver {
    static readonly DAYS_TO_MILLISECONDS_COEFFICIENT = 24 * 60 * 60 * 1000

    showMediatedBox = false
    showCourierDeliveryBox = false
    showAuthorizedPersonBox = true
    deliveryGroups: Array<Classifier> = []

    @Prop() ruleId!: string
    @Prop() typeRuleId!: string
    @Prop() documentType!: string
    @Prop() citizenPassportCourierCountryId!: string
    @Prop() citizenPassportCourierCityId!: string
    @Prop() citizenPassportCourierPostalCodeId!: string
    @Prop() authorizedPersonFirstNameField!: string
    @Prop() authorizedPersonLastNameField!: string
    @Prop() authorizedPersonCodeField!: string
    @Prop({ default: false }) showSubHeading!: boolean
    @Prop({ default: true }) showExpeditedCheckMark!: boolean
    @Prop({ default: true }) showAuthorizedPersonCheckMark!: boolean

    get applicationDocument (): ApplicationDocument {
        return ApplicationUtils.getDocument(this.documentType)
    }

    get headingKey (): string {
        return 'documents.' + this.documentType
    }

    get isWithAuthorizedPerson (): boolean {
        return this.applicationDocument.isWithAuthorized
    }

    get isCourierRetrieval (): boolean {
        return this.applicationDocument.delivery.type === ApplicationDeliveryType.COURIER_RETRIEVAL
    }

    get isApplicantAgeBetweenMinAndAdult (): boolean {
        return applicantStore.applicantAge != null && applicantStore.applicantAge >= APPLICANT_MIN_AGE &&
            applicantStore.applicantAge < ADULT_AGE
    }

    async placeOfIssueGroups (): Promise<Array<Classifier>> {
        const isStpRetrievalAllowed = await this.isStpRetrievalAllowed()
        const applicantMayUseAuthorizedPerson = await this.isUsingAuthorizedPersonAllowed(this.documentType)

        this.clearDelivery(!isStpRetrievalAllowed, ApplicationDeliveryType.STP_RETRIEVAL.toString())

        if (applicantMayUseAuthorizedPerson) {
            this.showAuthorizedPersonBox = true
        } else {
            this.showAuthorizedPersonBox = false
            this.clearAuthorizedPersonWhenUsingItNotAllowed()
        }

        return classifiersStore.placeOfIssueGroups
            .filter(classifier => classifier.code !== ApplicationDeliveryType.STP_RETRIEVAL.toString() ||
                isStpRetrievalAllowed)
            .filter(classifier => classifier.code !== ApplicationDeliveryType.COURIER_RETRIEVAL.toString() ||
                (this.documentType === DocumentType.CITIZEN_PASSPORT))
            .sort(classifier =>
                classifier.code === ApplicationDeliveryType.STP_RETRIEVAL.toString() ? -1 : 0
            )
    }

    private async isStpRetrievalAllowed (): Promise<boolean> {
        try {
            const stpValidDocumentsFilter = PlaceOfIssue.validDocumentsFilter(
                ENVIRONMENT.documentMusBeValidAtLeastDaysForStp, ENVIRONMENT.invalidDocumentTypesForStp)
            let validDocuments: Array<DocumentResponse>

            if (applicantStore.applicantRelationWithApplier !== ApplicantApplierRelationType.APPLICANT &&
                applicantStore.applicantAge !== null && applicantStore.applicantAge >= APPLICANT_MIN_AGE) {
                validDocuments = (await DocumentService.getValidDocumentsForRepresentative())
                    .filter(stpValidDocumentsFilter)
            } else {
                validDocuments = (await DocumentService.getValidDocuments())
                    .filter(stpValidDocumentsFilter)
            }

            // Stolen check is not needed for child with age less than 15
            if (applicantStore.applicantRelationWithApplier === ApplicantApplierRelationType.APPLICANT ||
                (applicantStore.applicantAge !== null && applicantStore.applicantAge >= APPLICANT_MIN_AGE)
            ) {
                return this.isMediatedIssuanceAllowed(validDocuments, this.documentType)
            }

            return !ObjectUtils.isEmpty(validDocuments)
        } catch {
            throw new GeneralError(['errors.system.title'])
        }
    }

    private async isCourierRetrievalAllowed (): Promise<boolean> {
        try {
            const courierValidDocumentsFilter = PlaceOfIssue.validDocumentsFilter(
                ENVIRONMENT.documentMusBeValidAtLeastDaysForCourier, ENVIRONMENT.invalidDocumentTypesForCourier)
            let validDocuments: Array<DocumentResponse>

            if (applicantStore.applicantRelationWithApplier !== ApplicantApplierRelationType.APPLICANT &&
                applicantStore.applicantAge !== null && applicantStore.applicantAge >= APPLICANT_MIN_AGE) {
                validDocuments = (await DocumentService.getValidDocumentsForRepresentative())
                    .filter(courierValidDocumentsFilter)
            } else {
                validDocuments = (await DocumentService.getValidDocuments())
                    .filter(courierValidDocumentsFilter)
            }

            // Stolen check is not needed for child with age less than 15
            if (applicantStore.applicantRelationWithApplier === ApplicantApplierRelationType.APPLICANT ||
                (applicantStore.applicantAge !== null && applicantStore.applicantAge >= APPLICANT_MIN_AGE)
            ) {
                validDocuments = validDocuments.filter(d => this.applicationDocument.reason !== ApplicationReason.LOST_STOLEN_DESTROYED ||
                    d.documentType !== ValidDocumentType.CITIZEN_PASSPORT)
            }

            return !ObjectUtils.isEmpty(validDocuments)
        } catch {
            throw new GeneralError(['errors.system.title'])
        }
    }

    private async isUsingAuthorizedPersonAllowed (documentType: string) {
        const applicationDocumentType = this.documentTypeAsValidDocumentType(documentType)
        if (applicantStore.applicantRelationWithApplier !== ApplicantApplierRelationType.APPLICANT) {
            return !this.isApplicantAgeBetweenMinAndAdult
        }
        if (applicationDocumentType === ValidDocumentType.CITIZEN_PASSPORT) {
            return applicantStore.getApplication.citizenPassport?.delivery.type !== ApplicationDeliveryType.COURIER_RETRIEVAL
        } else {
            return true
        }
    }

    isMediatedIssuanceAllowed (validDocuments: Array<DocumentResponse>, documentType: string): boolean {
        const documentsForRetrieval = applicantStore.validDocumentsForRetrieval(validDocuments)
        const applicationDocumentType = this.documentTypeAsValidDocumentType(documentType)

        if (documentsForRetrieval.length === 0) {
            return false
        } else if (documentsForRetrieval.length === 1 && this.applicationDocument.reason === ApplicationReason.LOST_STOLEN_DESTROYED) {
            return applicationDocumentType !== documentsForRetrieval[0].documentType
        } else {
            return true
        }
    }

    static validDocumentsFilter (validAtLeastDays: number, invalidDocumentTypes: Array<ValidDocumentType>):
        (document: DocumentResponse) => boolean {
        return (document: DocumentResponse) => {
            if (!document.dateOfExpiry || !document.documentType) {
                return false
            }

            const isValidDateOfExpiry = ((new Date(document.dateOfExpiry)).getTime() - Date.now()) >=
                validAtLeastDays * PlaceOfIssue.DAYS_TO_MILLISECONDS_COEFFICIENT
            const isValidDocumentType = !invalidDocumentTypes.includes(document.documentType)
            return isValidDateOfExpiry && isValidDocumentType
        }
    }

    get placeOfIssues (): Array<Classifier> {
        switch (this.applicationDocument.delivery.type) {
        case ApplicationDeliveryType.FOREIGN_RETRIEVAL:
            return classifiersStore.foreignPlacesOfIssues
        case ApplicationDeliveryType.PPA_RETRIEVAL:
            return classifiersStore.localPlacesOfIssues
        case ApplicationDeliveryType.CONSUL:
            return PlaceOfIssueUtils.getConsulPlaceOfIssues(this.documentType)
        case ApplicationDeliveryType.STP_RETRIEVAL:
            return classifiersStore.stpPlacesOfIssues
        default:
            return []
        }
    }

    async fetchDeliveryGroups (): Promise<void> {
        const groups: Array<Classifier> = await this.placeOfIssueGroups()

        if (groups.length === 1 || this.isExpedited) {
            this.applicationDocument.delivery.type = ApplicationDeliveryType.PPA_RETRIEVAL
            const result: Classifier | undefined = groups.find(a => a.code === 'PPA_RETRIEVAL')

            if (result !== undefined) {
                this.deliveryGroups = [result]
                return
            }
        }

        this.deliveryGroups = groups
    }

    clearDelivery (shouldClear: boolean, type: string): void {
        if (shouldClear && this.applicationDocument.delivery.type === type) {
            this.applicationDocument.delivery.type = null
            this.applicationDocument.delivery.location = null
            this.applicationDocument.delivery.courier = null
        }
    }

    clearCourierDelivery (): void {
        if (this.documentType === DocumentType.CITIZEN_PASSPORT && applicantStore.application.citizenPassport !== null) {
            applicantStore.application.citizenPassport.delivery.type = null
            applicantStore.application.citizenPassport.delivery.location = null
        }
    }

    clearAuthorizedPersonWhenUsingItNotAllowed (): void {
        if (this.applicationDocument.delivery.type === ApplicationDeliveryType.COURIER_RETRIEVAL &&
            this.applicationDocument.authorizedPerson != null) {
            this.applicationDocument.isWithAuthorized = false
            this.applicationDocument.authorizedPerson = null
        }
    }

    documentTypeAsValidDocumentType (documentType: string): string {
        if (documentType === DocumentType.ID_CARD) {
            return ValidDocumentType.ID_CARD.toString()
        } else {
            return ValidDocumentType.CITIZEN_PASSPORT.toString()
        }
    }

    get checkboxTranslationKey (): string {
        return `placeOfIssue.isExpedited.${this.documentType}`
    }

    get deliveryType (): string | null {
        return this.applicationDocument.delivery.type
    }

    get authorizedPersonCheckboxTranslationKey (): string {
        return `authorizedPerson.checkbox`
    }

    get expeditedTemporarilyStoppedTranslationKey (): string {
        return `placeOfIssue.isExpedited.temporarilyStopped.${this.documentType}`
    }

    get deliveryTimeTranslationKey (): string {
        const toForeignDepartmentEnding = this.applicationDocument.delivery.type !== ApplicationDeliveryType.PPA_RETRIEVAL &&
            !this.isExpedited
            ? 'ToForeignDep'
            : ''
        return `placeOfIssue.${this.isExpedited ? 'isExpedited.' : ''}${this.documentType === DocumentType.CITIZEN_PASSPORT
            ? 'deliveryTimePassport'
            : 'deliveryTimeCard'}${toForeignDepartmentEnding}`
    }

    get expeditedDeliveryTimeTranslationKey (): string {
        return `placeOfIssue.isExpedited.${this.documentType === DocumentType.CITIZEN_PASSPORT
            ? 'deliveryTimePassport'
            : 'deliveryTimeCard'}`
    }

    get isDeliveryTypeSelected (): boolean {
        return !!this.applicationDocument.delivery.type
    }

    get filteredPlaceOfIssues (): Array<Classifier> {
        return PlaceOfIssueUtils.filteredPlaceOfIssues(this.isExpedited, this.placeOfIssues)
    }

    get selectedPlaceOfIssue (): Classifier | undefined {
        return ClassifiersUtils.findClassifier(this.filteredPlaceOfIssues, this.applicationDocument.delivery.location)
    }

    get deliveryLocationGroup (): string {
        return this.applicationDocument.delivery.type || ''
    }

    get isExpedited (): boolean {
        return this.applicationDocument.expedited
    }

    get idReason (): string | null {
        return applicantStore.application.idCard ? applicantStore.application.idCard.reason : null
    }

    get passportReason (): string | null {
        return applicantStore.application.citizenPassport ? applicantStore.application.citizenPassport.reason : null
    }

    get labelTranslationKey (): string {
        return PlaceOfIssueUtils.deliveryGroupLabelTranslationKey(this.deliveryLocationGroup)
    }

    setGroup (value: string): void {
        if (value === ApplicationDeliveryType.STP_RETRIEVAL) {
            this.showMediatedModal()
        } else if (value === ApplicationDeliveryType.COURIER_RETRIEVAL) {
            this.showCourierDeliveryModal()
        }
        this.applicationDocument.delivery.type = value || null
    }

    setSelectedPlaceOfIssue (classifierCode: string): void {
        this.applicationDocument.delivery.location = classifierCode
    }

    setIsExpedited (): void {
        if (!this.isExpedited) {
            this.showExpeditedModal()
        } else {
            this.applicationDocument.expedited = false
        }
    }

    setIsWithAuthorized (): void {
        if (this.isWithAuthorizedPerson) {
            this.applicationDocument.isWithAuthorized = false
            this.applicationDocument.authorizedPerson = null
        } else {
            this.applicationDocument.isWithAuthorized = true
            this.applicationDocument.authorizedPerson = new ApplicationAuthorizedPersonEntity()
        }
    }

    showExpeditedModal (): void {
        $('#isExpeditedModal-' + this.documentType).modal('show')
    }

    showMediatedModal (): void {
        $('#mediatedIssuancePricingModal-' + this.documentType).modal('show')
        this.showMediatedBox = true
    }

    showCourierDeliveryModal (): void {
        $('#courierDeliveryModal-' + this.documentType).modal('show')
        this.showCourierDeliveryBox = true
    }

    updateCourierWhenPlaceOfIssueChanged () {
        if (!this.isCourierRetrieval) {
            this.applicationDocument.delivery.courier = null
            this.showCourierDeliveryBox = false
        } else {
            this.showCourierDeliveryBox = true
        }
    }

    validatePlaceOfIssue (): void {
        (this.$refs.placeOfIssueValidator as ProviderInstance).validate()
    }

    translateError (validationError: ValidationError): string {
        return translateValidationError(validationError)
    }

    @Watch('isExpedited', { immediate: true })
    onExpeditedStatusChanged () {
        this.fetchDeliveryGroups()
        this.$forceUpdate()
    }

    @Watch('idReason', { immediate: true })
    onIdReasonChanged () {
        this.fetchDeliveryGroups()
        this.$forceUpdate()
    }

    @Watch('passportReason', { immediate: true })
    onPassReasonChanged () {
        this.fetchDeliveryGroups()
        this.$forceUpdate()
    }

    @Watch('deliveryLocationGroup', { immediate: true })
    onPlaceOfIssueChanged () {
        this.updateCourierWhenPlaceOfIssueChanged()
        this.fetchDeliveryGroups()
        this.$forceUpdate()
    }

    mounted (): void {
        this.fetchDeliveryGroups()
    }
}
